import { IPropertyEditable } from "../../../../base/domain/system/edit";
import {
  IPrintBehavior,
  IQuoteBehavior,
  IFreeInputBehavior,
  INaturalTextBehavior,
  ITextSearchBehavior,
  IGroupAlertBehavior,
  IEmptyAlertBehavior,
  ILengthAlertBehavior,
  ITooltipBehavior,
  IMultiLineBehavior,
  IInputRuleBehavior,
  IInputMethodBehavior,
  InputMethodKinds,
  IObjectsControllable,
  ILinkedPageObjectSpecifiable,
  ILinkedObjectGroupSpecifiable,
  TranscribeTargetKinds,
} from "../../../../documaker/domain/format/objects";
import { IFormatSpecification } from "../IFormatSpecification";
import { IStandardTextDrawableObject } from "../objects";
import {
  IPrintBehaviorPropertyEditable,
  IQuoteBehaviorPropertyEditable,
  IFreeInputBehaviorPropertyEditable,
  INaturalTextBehaviorPropertyEditable,
  ITextSearchBehaviorPropertyEditable,
  IGroupAlertBehaviorPropertyEditable,
  IEmptyAlertBehaviorPropertyEditable,
  ILengthAlertBehaviorPropertyEditable,
  ITooltipBehaviorPropertyEditable,
  IMultiLineBehaviorPropertyEditable,
  IInputRuleBehaviorPropertyEditable,
  IInputMethodBehaviorPropertyEditable,
  ITranscribeBehaviorPropertyEditable,
} from "../properties";
import { AbstractTextDrawableObjectEditor } from "./AbstractTextDrawableObjectEditor";

export abstract class AbstractStandardTextDrawableObjectEditor<TObject extends IStandardTextDrawableObject> extends AbstractTextDrawableObjectEditor<TObject> implements
IPrintBehaviorPropertyEditable,
IQuoteBehaviorPropertyEditable,
IFreeInputBehaviorPropertyEditable,
INaturalTextBehaviorPropertyEditable,
ITextSearchBehaviorPropertyEditable,
IGroupAlertBehaviorPropertyEditable,
IEmptyAlertBehaviorPropertyEditable,
ILengthAlertBehaviorPropertyEditable,
ITooltipBehaviorPropertyEditable,
IMultiLineBehaviorPropertyEditable,
IInputRuleBehaviorPropertyEditable,
IInputMethodBehaviorPropertyEditable,
ITranscribeBehaviorPropertyEditable {
  constructor(source: TObject | AbstractStandardTextDrawableObjectEditor<TObject>, specification?: IFormatSpecification, isNew: boolean = false) {
    super(source, specification, isNew);
    this._id = this.createIdPropertyEditor();
    this._name = this.createNamePropertyEditor();
    this._nameHistories = this.createNameHistoriesPropertyEditor();
    this._bookmark = this.createBookmarkPropertyEditor();
    this._printBehavior = this.createPropertyEditor(s => s.printBehavior, (s, v) => s.printBehavior = v);
    this._quoteBehavior = this.createPropertyEditor(s => s.quoteBehavior, (s, v) => s.quoteBehavior = v);
    this._freeInputBehavior = this.createPropertyEditor(s => s.freeInputBehavior, (s, v) => s.freeInputBehavior = v);
    this._naturalTextBehavior = this.createPropertyEditor(s => s.naturalTextBehavior, (s, v) => s.naturalTextBehavior = v);
    this._textSearchBehavior = this.createPropertyEditor(s => s.textSearchBehavior, (s, v) => s.textSearchBehavior = v);
    this._groupAlertBehavior = this.createPropertyEditor(s => s.groupAlertBehavior, (s, v) => s.groupAlertBehavior = v);
    this._emptyAlertBehavior = this.createPropertyEditor(s => s.emptyAlertBehavior, (s, v) => s.emptyAlertBehavior = v);
    this._lengthAlertBehavior = this.createPropertyEditor(s => s.lengthAlertBehavior, (s, v) => s.lengthAlertBehavior = v);
    this._tooltipBehavior = this.createPropertyEditor(s => s.tooltipBehavior, (s, v) => s.tooltipBehavior = v);
    this._multiLineBehavior = this.createMultiLineBehaviorPropertyEditor(s => false);                       // 既定は編集不可に.必要な派生クラスでoverrideする想定.
    this._inputRuleBehavior = this.createInputRuleBehaviorPropertyEditor(s => false);                       // 既定は編集不可に.必要な派生クラスでoverrideする想定.
    this._inputMethodBehavior = this.createInputMethodBehaviorPropertyEditor(s => false);                   // 既定は編集不可に.必要な派生クラスでoverrideする想定.
    this._scriptExecuteTriggerBehavior = this.createScriptExecuteTriggerBehaviorPropertyEditor(s => false); // 既定は編集不可に.必要な派生クラスでoverrideする想定.
    this._transcribeBehavior = this.createTranscribeBehaviorPropertyEditor(s => (0 < this.getSelectableTranscribeTargets().length));  // 既定は選択可能な転記先がある場合のみ編集可に.必要な派生クラスでoverrideする想定.
    this._emptyAlertAllowBehavior = this.createEmptyAlertAllowBehaviorPropertyEditor(s => false);           // 既定は編集不可に.必要な派生クラスでoverrideする想定.
    this._groupAlertAllowBehavior = this.createGroupAlertAllowBehaviorPropertyEditor(s => false);           // 既定は編集不可に.必要な派生クラスでoverrideする想定.
  }

  protected _printBehavior: IPropertyEditable<IPrintBehavior>;
  protected _quoteBehavior: IPropertyEditable<IQuoteBehavior>;
  protected _freeInputBehavior: IPropertyEditable<IFreeInputBehavior>;
  protected _naturalTextBehavior: IPropertyEditable<INaturalTextBehavior>;
  protected _textSearchBehavior: IPropertyEditable<ITextSearchBehavior>;
  protected _groupAlertBehavior: IPropertyEditable<IGroupAlertBehavior>;
  protected _emptyAlertBehavior: IPropertyEditable<IEmptyAlertBehavior>;
  protected _lengthAlertBehavior: IPropertyEditable<ILengthAlertBehavior>;
  protected _tooltipBehavior: IPropertyEditable<ITooltipBehavior>;
  protected _multiLineBehavior: IPropertyEditable<IMultiLineBehavior>;
  protected _inputRuleBehavior: IPropertyEditable<IInputRuleBehavior>;
  protected _inputMethodBehavior: IPropertyEditable<IInputMethodBehavior>;
  protected _scriptExecuteTriggerBehavior: IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>>;
  protected _transcribeBehavior: IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>>;
  protected _emptyAlertAllowBehavior: IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>>;
  protected _groupAlertAllowBehavior: IPropertyEditable<IObjectsControllable<ILinkedObjectGroupSpecifiable>>;

  protected createMultiLineBehaviorPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<IMultiLineBehavior> {
    return this.createPropertyEditor(
      s => s.multiLineBehavior,
      (s, v) => s.multiLineBehavior = v,
      isWritable ? isWritable : s => true);
  }

  protected createInputRuleBehaviorPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<IInputRuleBehavior> {
    return this.createPropertyEditor(
      s => s.inputRuleBehavior,
      (s, v) => s.inputRuleBehavior = v,
      isWritable ? isWritable : s => true);
  }

  protected createInputMethodBehaviorPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<IInputMethodBehavior> {
    return this.createPropertyEditor(
      s => s.inputMethodBehavior,
      (s, v) => s.inputMethodBehavior = v,
      isWritable ? isWritable : s => true);
  }

  protected createScriptExecuteTriggerBehaviorPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>> {
    return this.createPropertyEditor(
      s => s.scriptExecuteTriggerBehavior,
      (s, v) => s.scriptExecuteTriggerBehavior = v,
      isWritable ? isWritable : s => true);
  }

  protected createTranscribeBehaviorPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>> {
    return this.createPropertyEditor(
      s => s.transcribeBehavior,
      (s, v) => s.transcribeBehavior = v,
      isWritable ? isWritable : s => true);
  }

  protected createEmptyAlertAllowBehaviorPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>> {
    return this.createPropertyEditor(
      s => s.emptyAlertAllowBehavior,
      (s, v) => s.emptyAlertAllowBehavior = v,
      isWritable ? isWritable : s => true);
  }

  protected createGroupAlertAllowBehaviorPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<IObjectsControllable<ILinkedObjectGroupSpecifiable>> {
    return this.createPropertyEditor(
      s => s.groupAlertAllowBehavior,
      (s, v) => s.groupAlertAllowBehavior = v,
      isWritable ? isWritable : s => true);
  }

  printBehavior(): IPropertyEditable<IPrintBehavior> {
    return this._printBehavior;
  }

  quoteBehavior(): IPropertyEditable<IQuoteBehavior> {
    return this._quoteBehavior;
  }

  freeInputBehavior(): IPropertyEditable<IFreeInputBehavior> {
    return this._freeInputBehavior;
  }

  naturalTextBehavior(): IPropertyEditable<INaturalTextBehavior> {
    return this._naturalTextBehavior;
  }

  textSearchBehavior(): IPropertyEditable<ITextSearchBehavior> {
    return this._textSearchBehavior;
  }

  groupAlertBehavior(): IPropertyEditable<IGroupAlertBehavior> {
    return this._groupAlertBehavior;
  }

  emptyAlertBehavior(): IPropertyEditable<IEmptyAlertBehavior> {
    return this._emptyAlertBehavior;
  }

  lengthAlertBehavior(): IPropertyEditable<ILengthAlertBehavior> {
    return this._lengthAlertBehavior;
  }

  tooltipBehavior(): IPropertyEditable<ITooltipBehavior> {
    return this._tooltipBehavior;
  }

  multiLineBehavior(): IPropertyEditable<IMultiLineBehavior> {
    return this._multiLineBehavior;
  }

  inputRuleBehavior(): IPropertyEditable<IInputRuleBehavior> {
    return this._inputRuleBehavior;
  }

  inputMethodBehavior(): IPropertyEditable<IInputMethodBehavior> {
    return this._inputMethodBehavior;
  }

  getSelectableInputMethods(): InputMethodKinds[] {
    return this._specification.objects.tag.inputMethods;
  }

  scriptExecuteTriggerBehavior(): IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>> {
    return this._scriptExecuteTriggerBehavior;
  }

  transcribeBehavior(): IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>> {
    return this._transcribeBehavior;
  }

  getSelectableTranscribeTargets(): TranscribeTargetKinds[] {
    return [];  // 選択できるオブジェクトでオーバーライドする想定.
  }

  emptyAlertAllowBehavior(): IPropertyEditable<IObjectsControllable<ILinkedPageObjectSpecifiable>> {
    return this._emptyAlertAllowBehavior;
  }

  groupAlertAllowBehavior(): IPropertyEditable<IObjectsControllable<ILinkedObjectGroupSpecifiable>> {
    return this._groupAlertAllowBehavior;
  }
}
